import * as React from 'react'
import { connect, FormikProps, getIn } from 'formik'
import * as O from 'fp-ts/lib/Option'

import { metaProp } from '@espoon-voltti/ts-libs/lib/validation'

import {
  Column,
  Columns,
  Title
} from '@espoon-voltti/react-components/elements'
import { SumField } from '@espoon-voltti/react-components/elements/form'
import {
  fieldState,
  ErrorMessage
} from '@espoon-voltti/react-voltti/components/form/formik-util'

import {
  FormField,
  FormEuroField,
  FormDateField,
  FormTextArea,
  FormIntegerField
} from '@espoon-voltti/react-voltti/components/form'

import { AgeDistribution } from '@espoon-voltti/nuora-domain/grants'

import { ProjectGrantDraftApp } from '../../../../domain'
import { t } from '../../../../i18n'

import { eurosMax } from '../../validation'
import { maybeProjectGrantForm } from '../../support'

export const AppDetails = () => {
  return (
    <div>
      <Columns multiline>
        <Column desktopSize="one-third" tabletSize="half">
          <FormField
            name="form.event.name"
            label={t(['views', 'form', 'appDetails', 'event', 'name'])}
            required={true}
            dataQa="eventName"
          />
        </Column>
        <Column desktopSize="one-third" tabletSize="half">
          <FormField
            name="form.event.location"
            label={t(['views', 'form', 'appDetails', 'event', 'location'])}
            dataQa="eventLocation"
          />
        </Column>

        <Column desktopSize={2} tabletSize="half">
          <FormDateField
            name="form.event.startDate"
            dataQa="eventStartDate"
            label={t(['views', 'form', 'appDetails', 'event', 'startDate'])}
          />
        </Column>
        <Column desktopSize={2} tabletSize="half">
          <FormDateField
            name="form.event.endDate"
            dataQa="eventEndDate"
            label={t(['views', 'form', 'appDetails', 'event', 'endDate'])}
          />
        </Column>
        <Column desktopSize="two-thirds" tabletSize="half">
          <FormTextArea
            name="form.purpose"
            dataQa="purpose"
            label={t(['views', 'form', 'appDetails', 'purpose'])}
            required={true}
          />
        </Column>

        <Column desktopSize={2} tabletSize="half">
          <FormEuroField
            required
            name="form.appliedSum"
            dataQa="appliedSum"
            max={eurosMax.euros()}
            label={t(['views', 'form', 'appDetails', 'appliedTotal'])}
          />
        </Column>
      </Columns>

      <Title size={3}>
        {t(['views', 'form', 'appDetails', 'numberOfParticipants'])}
      </Title>

      <Columns>
        <Column size="one-fifth">
          <FormIntegerField
            name="form.participants.ages7to20"
            dataQa="participantsAges7to20"
            label={t([
              'views',
              'form',
              'appDetails',
              'participants',
              'ages7to20',
              'label'
            ])}
          />
        </Column>
        <Column size="one-fifth">
          <FormIntegerField
            name="form.participants.ages21to28"
            dataQa="participantsAges21to28"
            label={t([
              'views',
              'form',
              'appDetails',
              'participants',
              'ages21to28',
              'label'
            ])}
          />
        </Column>
        <Column size="one-fifth">
          <FormIntegerField
            name="form.participants.ages29andOver"
            dataQa="participantsAges29andOver"
            label={t([
              'views',
              'form',
              'appDetails',
              'participants',
              'ages29andOver',
              'label'
            ])}
          />
        </Column>

        <Column size="one-fifth" align="end">
          <ParticipantsTotal />
        </Column>
      </Columns>
    </div>
  )
}

const ParticipantsTotal = connect<{}, ProjectGrantDraftApp>(
  ({ formik }: { formik: FormikProps<ProjectGrantDraftApp> }) => {
    const form = maybeProjectGrantForm(formik.values.form)

    // TODO: General grant
    if (O.isNone(form)) {
      return null
    }

    const fieldName = 'form.participants'
    const totalFieldName = metaProp(fieldName)
    const touched = !!Object.keys(form.value.participants).find(
      (x) => !!getIn(formik.touched, `${fieldName}.${x}`)
    )
    const state = fieldState(totalFieldName, formik, touched)

    return (
      <SumField
        dataQa="participantsTotal"
        message={<ErrorMessage name={totalFieldName} touched={touched} />}
        state={state}
      >
        <span data-qa="label">
          {t(['views', 'form', 'appDetails', 'participants', 'total', 'label'])}
          :{' '}
        </span>
        <span data-qa="sum">{total(form.value.participants)}</span>
      </SumField>
    )
  }
)

const total = (x: AgeDistribution) =>
  x.ages7to20 + x.ages21to28 + x.ages29andOver
